import { useCollection } from "@/composables/useFirestore";
import { useI18n } from "@/composables/useI18n";
import { db } from "@/firebase";
import type { Account } from "@aircap/common/account";
import {
  getTreezorOperationCounterparty,
  getTreezorOperationLabelAndReference,
  treezorBeneficiariesFullPath,
  treezorOperationFullPath,
  treezorOperationsFullPath,
  type Beneficiary,
  type Operation,
} from "@aircap/common/treezor";
import { and, collection, doc, or, orderBy, query, updateDoc, where } from "firebase/firestore";
import { computed, type Ref } from "vue";
import type { VueFirestoreDocumentData } from "vuefire";
import { formatCurrency } from "../utils/tools.js";
import type { Transaction } from "../utils/types.js";

export const useTreezorTransactions = (
  account: Ref<VueFirestoreDocumentData<Account> | undefined>,
  filters?: {
    beneficiaryIban?: Ref<string | undefined>;
    isFuture?: Ref<boolean>;
  },
) => {
  const { t: $t, locale } = useI18n();

  const beneficiariesSource = computed(() => {
    return account.value?.id
      ? collection<Beneficiary>(db, treezorBeneficiariesFullPath(account.value.id))
      : null;
  });
  const { data: beneficiaries } = useCollection<Omit<Beneficiary, "id"> & { id: string }>(
    beneficiariesSource,
  );
  const beneficiariesMap = computed(() => {
    return new Map(beneficiaries.value.map((b) => [b.id, b]));
  });

  const transactionsSource = computed(() => {
    if (account.value?.accountType !== "treezor") return null;

    let beneficiaryId;
    if (filters?.beneficiaryIban?.value) {
      const beneficiaryIban = filters.beneficiaryIban.value;
      beneficiaryId = beneficiaries.value.find((b) => b.metadata?.iban === beneficiaryIban)?.id;
    }

    return query(
      collection<Operation>(db, treezorOperationsFullPath(account.value.id)),
      and(
        where<Operation>("status", "in", ["AUTHORIZED", "SETTLED"]),
        ...(filters?.isFuture?.value === false
          ? [where<Operation>("status", "in", ["SETTLED"])]
          : []),
        ...(filters?.isFuture?.value === true
          ? [where<Operation>("status", "in", ["AUTHORIZED"])]
          : []),
        ...(filters?.isFuture?.value === undefined
          ? [where<Operation>("status", "in", ["AUTHORIZED", "SETTLED"])]
          : []),
        // Should match logic in getTreezorOperationCounterparty
        or(
          ...(beneficiaryId
            ? [
                where<{ metadata: { beneficiaryId: string } }>(
                  "metadata.beneficiaryId",
                  "==",
                  beneficiaryId,
                ),
              ]
            : []),
          ...(filters?.beneficiaryIban?.value
            ? [
                where<{ metadata: { dbtrIBAN: string } }>(
                  "metadata.dbtrIBAN",
                  "==",
                  filters.beneficiaryIban.value,
                ),
              ]
            : []),
        ),
      ),
      orderBy<Operation>("date.creation", "desc"),
    );
  });

  const { data: transactionCollection, pending: loading } =
    useCollection<Operation>(transactionsSource);

  const transactions = computed<Transaction[]>(() => {
    return transactionCollection.value.map((t) => {
      const counterparty = getTreezorOperationCounterparty(t, beneficiariesMap.value);
      const { label, reference } = getTreezorOperationLabelAndReference(t);

      return {
        id: t.id,
        date: t.date.creation,
        name: t.label || "",
        amount: (t.amount.amount / 100) * (t.direction === "CREDIT" ? 1 : -1),
        currencyCode: t.amount.currency,
        counterparty: counterparty?.name,
        counterpartyIban: counterparty?.iban,
        category: t.categoryId
          ? { id: t.categoryId, name: $t(`categories.${t.categoryId}`) }
          : undefined,
        trackedCategorySplits: t.trackedCategorySplits,
        suggestedTrackedCategoryId: t.suggestedTrackedCategoryId,
        rejectedSuggestedTrackedCategoryId: t.rejectedSuggestedTrackedCategoryId,
        amountFormatted: formatCurrency({
          value: (t.amount.amount / 100) * (t.direction === "CREDIT" ? 1 : -1),
          currencyCode: t.amount.currency,
          sign: true,
          locale: locale.value,
        }),
        is_future: t.status === "AUTHORIZED",
        dateFormatted: {
          short: new Intl.DateTimeFormat(locale.value).format(new Date(t.date.creation)),
          long: new Intl.DateTimeFormat(locale.value, {
            dateStyle: "long",
            timeStyle: "short",
          }).format(new Date(t.date.creation)),
        },
        is_flagged: t.is_flagged,
        label,
        reference,
        transferRequest: t.transferRequest
          ? {
              ...t.transferRequest,
              transactionId: t.transferRequest.treezorOperationId,
              files: t.transferRequest.files,
              label: t.transferRequest.label,
              reference: t.transferRequest.reference,
              trackedCategoryId: t.transferRequest.trackedCategoryId,
              decisions: t.transferRequest.decisions,
            }
          : undefined,
        files: [
          ...(t.files?.map((f) => ({
            ...f,
            source: "transaction" as const,
          })) ?? []),
          ...(t.transferRequest?.files?.map((f) => ({
            ...f,
            source: "transferRequest" as const,
          })) ?? []),
        ],
      } satisfies Transaction;
    });
  });

  const flagTransaction = (transactionId: string, value: boolean) => {
    if (!account.value) throw new Error("no project set");
    const transactionRef = doc<Operation>(
      db,
      treezorOperationFullPath(account.value.id, transactionId),
    );
    void updateDoc(transactionRef, { is_flagged: value });
  };

  return {
    transactions,
    loading,
    flagTransaction,
  };
};
